import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

/**
 * Presentational + logic panel for the optional email-OTP MFA toggle.
 * Agnostic of which axios client is used - the parent supplies the four
 * API calls so this same component works for both the tenant admin
 * dashboard (`api`) and the platform admin dashboard (`platformApi`).
 */
export default function MfaSettingsPanel({ getStatus, startEnroll, verifyEnroll, disableMfa }) {
  const [loading, setLoading] = useState(true);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollStep, setEnrollStep] = useState('start'); // 'start' | 'verify'
  const [enrollChallengeId, setEnrollChallengeId] = useState('');
  const [enrollCode, setEnrollCode] = useState('');

  const [disableOpen, setDisableOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const response = await getStatus();
      setMfaEnabled(!!response.data.mfa_enabled);
    } catch (error) {
      toast.error('Failed to load two-factor authentication status');
    } finally {
      setLoading(false);
    }
  };

  const openEnroll = () => {
    setEnrollStep('start');
    setEnrollCode('');
    setEnrollOpen(true);
  };

  const handleSendEnrollCode = async () => {
    setBusy(true);
    try {
      const response = await startEnroll();
      setEnrollChallengeId(response.data.challenge_id);
      setEnrollStep('verify');
      toast.success('Verification code sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyEnroll = async () => {
    if (!enrollCode || enrollCode.length !== 6) {
      toast.error('Enter the 6-digit code sent to your email');
      return;
    }
    setBusy(true);
    try {
      await verifyEnroll(enrollChallengeId, enrollCode);
      setMfaEnabled(true);
      setEnrollOpen(false);
      toast.success('Two-factor authentication is now enabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    if (!currentPassword) {
      toast.error('Enter your current password to confirm');
      return;
    }
    setBusy(true);
    try {
      await disableMfa(currentPassword);
      setMfaEnabled(false);
      setDisableOpen(false);
      setCurrentPassword('');
      toast.success('Two-factor authentication has been turned off');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not turn off two-factor authentication');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12" data-testid="mfa-settings-loading">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#4A6741]" />
      </div>
    );
  }

  return (
    <div data-testid="mfa-settings-panel">
      <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
        Security
      </h2>
      <p className="text-gray-500 mb-8 text-sm max-w-2xl">
        Two-factor authentication adds a one-time email code on top of your password when
        signing in. It's optional and off by default - enable it anytime for extra protection.
      </p>

      <div className="bg-gray-50 rounded-lg border border-[#F3F4F6] p-6 max-w-lg">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${mfaEnabled ? 'bg-[#4A6741]/10' : 'bg-gray-200'}`}>
              {mfaEnabled ? (
                <ShieldCheck className="h-5 w-5 text-[#4A6741]" />
              ) : (
                <ShieldOff className="h-5 w-5 text-gray-500" />
              )}
            </div>
            <div>
              <p className="font-bold text-gray-900">Email Two-Factor Authentication</p>
              <p className="text-sm text-gray-500 mt-1">
                {mfaEnabled
                  ? 'Enabled - a code is emailed to you at every sign-in.'
                  : 'Disabled - sign in with just your password.'}
              </p>
            </div>
          </div>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${
              mfaEnabled ? 'bg-[#4A6741]/10 text-[#4A6741]' : 'bg-gray-200 text-gray-600'
            }`}
            data-testid="mfa-status-badge"
          >
            {mfaEnabled ? 'ON' : 'OFF'}
          </span>
        </div>

        <div className="mt-5">
          {mfaEnabled ? (
            <Button
              variant="outline"
              onClick={() => setDisableOpen(true)}
              className="border-red-200 text-red-600 hover:bg-red-50"
              data-testid="mfa-disable-btn"
            >
              Turn Off Two-Factor Authentication
            </Button>
          ) : (
            <Button
              onClick={openEnroll}
              className="bg-[#4A6741] hover:bg-[#3d5636]"
              data-testid="mfa-enable-btn"
            >
              Turn On Two-Factor Authentication
            </Button>
          )}
        </div>
      </div>

      {/* Enroll Dialog */}
      <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Turn On Two-Factor Authentication</DialogTitle>
          </DialogHeader>
          {enrollStep === 'start' ? (
            <div className="py-4">
              <p className="text-sm text-gray-600">
                We'll email a 6-digit verification code to your account email to confirm it's you
                before turning this on.
              </p>
            </div>
          ) : (
            <div className="py-4 space-y-3">
              <Label htmlFor="enroll-code">Verification Code</Label>
              <Input
                id="enroll-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={enrollCode}
                onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, ''))}
                className="bg-gray-50 border-[#F3F4F6] text-center text-xl tracking-[0.4em]"
                data-testid="mfa-enroll-code-input"
              />
              <p className="text-xs text-gray-500">Check your email for the code. It expires in 10 minutes.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollOpen(false)}>Cancel</Button>
            {enrollStep === 'start' ? (
              <Button
                onClick={handleSendEnrollCode}
                disabled={busy}
                className="bg-[#4A6741] hover:bg-[#3d5636]"
                data-testid="mfa-enroll-send-code-btn"
              >
                {busy ? 'Sending...' : 'Send Code'}
              </Button>
            ) : (
              <Button
                onClick={handleVerifyEnroll}
                disabled={busy}
                className="bg-[#4A6741] hover:bg-[#3d5636]"
                data-testid="mfa-enroll-verify-btn"
              >
                {busy ? 'Verifying...' : 'Verify & Enable'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Turn Off Two-Factor Authentication</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <Label htmlFor="disable-current-password">Confirm Your Password</Label>
            <Input
              id="disable-current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="bg-gray-50 border-[#F3F4F6]"
              data-testid="mfa-disable-password-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDisable}
              disabled={busy}
              data-testid="mfa-disable-confirm-btn"
            >
              {busy ? 'Turning off...' : 'Turn Off'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
